/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * S725 -- old-store (AbleCommerce) order history API.
 *
 * GET legacy-orders/                 -> { enabled, orders }
 * GET legacy-orders/<order_number>/  -> { enabled, full_details,
 *                                        sign_in_to_see_full_details, order }
 *                                        or 404
 *
 * A 503 is the old store's circuit breaker answering "not now" (Retry-After:
 * 60), so it is NOT retried by the shared base query: retrying cannot change
 * the answer, and every attempt spends one of the customer's sixty hourly
 * requests (LO-3, LO-17).
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import type {
  LegacyOrderDetailResponse,
  LegacyOrderListResponse,
} from "@/types/legacy-orders";
import {
  baseQueryWithReauth,
  CACHE_TIMES,
  TAG_TYPES,
} from "@/redux/api-config";

/** The status the backend uses for "the old store is unavailable right now". */
export const LEGACY_ORDERS_UNAVAILABLE_STATUS = 503;

const NO_RETRY_ON_UNAVAILABLE = {
  doNotRetryStatuses: [LEGACY_ORDERS_UNAVAILABLE_STATUS],
} as const;

export const legacyOrdersApi = createApi({
  reducerPath: "legacyOrdersApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [TAG_TYPES.LEGACY_ORDERS],
  // Old-store orders never change; the server caches them for ten minutes too.
  keepUnusedDataFor: CACHE_TIMES.MEDIUM,
  endpoints: (builder) => ({
    getLegacyOrders: builder.query<LegacyOrderListResponse, void>({
      query: () => "legacy-orders/",
      extraOptions: NO_RETRY_ON_UNAVAILABLE,
      providesTags: [{ type: TAG_TYPES.LEGACY_ORDERS, id: "LIST" }],
    }),
    getLegacyOrder: builder.query<LegacyOrderDetailResponse, number>({
      query: (orderNumber) =>
        `legacy-orders/${encodeURIComponent(String(orderNumber))}/`,
      extraOptions: NO_RETRY_ON_UNAVAILABLE,
      providesTags: (_result, _error, orderNumber) => [
        { type: TAG_TYPES.LEGACY_ORDERS, id: orderNumber },
      ],
    }),
  }),
});

export const { useGetLegacyOrdersQuery, useGetLegacyOrderQuery } =
  legacyOrdersApi;
